import React, { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import RadioGroup from "aldoo-ra/RadioGroup"
import Tabs from "aldoo-ra/Tabs"
import FileUpload from "aldoo-ra/FileUpload"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import Panel from "aldoo-ra/Panel"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { GetStoreProducts, GetAllModels } from "../../api"
import { renderPlatformIcon, getPlatformDisplayName } from "aldoo-ra/Admin/utils/platformIconUtils"

const SettingsStep = ({ formData, errors, onInputChange, selectedModel }) => {
  const [customCodesTab, setCustomCodesTab] = useState("enter")
  const [codesSource, setCodesSource] = useState("manual") // Track if codes came from file or manual entry
  const [uploadedFileName, setUploadedFileName] = useState("") // Store the uploaded file name
  const [fileUploadKey, setFileUploadKey] = useState(0) // Key to reset FileUpload component
  const [productOptions, setProductOptions] = useState([])
  const [loadingProducts, setLoadingProducts] = useState(false)
  const [modelOptions, setModelOptions] = useState([])
  const [loadingModels, setLoadingModels] = useState(false)


  // Load store products
  const loadStoreProducts = async (search = "") => {
    try {
      setLoadingProducts(true)
      const response = await GetStoreProducts({
        search,
        itemsPerPage: -1,
      })

      if (response.result && response.data) {
        const options = response.data.map((product) => ({
          value: product.pid,
          label: product.name,
        }))
        setProductOptions(options)
      }
    } catch (error) {
    } finally {
      setLoadingProducts(false)
    }
  }

  // Load all models
  const loadModels = async () => {
    try {
      setLoadingModels(true)
      const response = await GetAllModels()

      if (Array.isArray(response)) {
        const options = response.map((model) => ({
          value: model._id,
          label: model.name,
          description: `${model.codePattern} • ${model.codeSet}`,
          codePattern: model.codePattern,
          codeSet: model.codeSet,
        }))
        setModelOptions(options)
      }
    } catch (error) {
    } finally {
      setLoadingModels(false)
    }
  }

  // Load products and models on component mount
  useEffect(() => {
    loadStoreProducts()
    loadModels()
  }, [])

  // Populate code generation fields when coming from step 2 with a selected model
  useEffect(() => {
    // If we have a selected model from step 2, models are loaded, and no code generation model is set yet
    if (selectedModel && modelOptions.length > 0 && !formData.codeGenerationModelId) {
      // Check if the selected model exists in the loaded models
      const modelExists = modelOptions.some(opt => opt.value === selectedModel._id)
      if (modelExists) {
        // Set the code generation model to the selected model from step 2
        onInputChange("codeGenerationModelId", selectedModel._id)
        onInputChange("codePattern", selectedModel.codePattern)
        onInputChange("codeSet", selectedModel.codeSet)
      }
    }
  }, [selectedModel, formData.codeGenerationModelId, modelOptions, onInputChange])

  // Reset file upload component
  const resetFileUpload = () => {
    setFileUploadKey((prev) => prev + 1)
  }

  // Handle file upload and validation
  const handleFileUpload = (fileOrFiles) => {
    if (!fileOrFiles) return

    // Handle both single file and array of files
    const file = Array.isArray(fileOrFiles) ? fileOrFiles[0] : fileOrFiles
    if (!file) return
    const validExtensions = [".txt", ".csv"]
    const fileExtension = "." + file.name.split(".").pop().toLowerCase()

    if (!validExtensions.includes(fileExtension)) {
      showPromptDialog({
        title: "Invalid File Format",
        message:
          "Please upload a .txt or .csv file containing activation codes (one per line).",
        showYesBtn: true,
        showNoBtn: false,
        yesLabel: "OK",
        onConfirm: () => {
          resetFileUpload()
        },
      })
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const content = e.target.result
        let rawCodes = content
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter((line) => line.length > 0) // Remove empty lines

        // Check if first line looks like a CSV header
        if (rawCodes.length > 0 && fileExtension === ".csv") {
          const firstLine = rawCodes[0]
          // Common header patterns
          if (
            firstLine.toLowerCase().includes("key") ||
            firstLine.toLowerCase().includes("code") ||
            firstLine.toLowerCase().includes("status") ||
            firstLine.toLowerCase().includes("activation")
          ) {
            // Skip the header line
            rawCodes = rawCodes.slice(1)
          }
        }

        // For CSV files, extract only the first column (the key/code)
        if (fileExtension === ".csv") {
          rawCodes = rawCodes.map(line => {
            const parts = line.split(",")
            return parts[0].trim()
          }).filter(code => code.length > 0)
        }

        if (rawCodes.length === 0) {
          showPromptDialog({
            title: "Empty File",
            message:
              "The uploaded file does not contain any valid activation codes. Please ensure each code is on a separate line.",
            showYesBtn: true,
            showNoBtn: false,
            yesLabel: "OK",
            onConfirm: () => {
              resetFileUpload()
            },
          })
          return
        }

        // Remove duplicates by converting to Set and back to array
        const uniqueCodes = [...new Set(rawCodes)]
        const duplicatesCount = rawCodes.length - uniqueCodes.length

        // Show warning if duplicates were removed
        if (duplicatesCount > 0) {
          showPromptDialog({
            title: "Duplicate Codes Removed",
            message: `Found ${duplicatesCount} duplicate code${
              duplicatesCount > 1 ? "s" : ""
            }. Only unique codes will be imported.\n\nUnique codes: ${
              uniqueCodes.length
            }\nDuplicates removed: ${duplicatesCount}`,
            showYesBtn: true,
            showNoBtn: false,
            yesLabel: "OK",
            onConfirm: () => {},
          })
        }

        // Update the form data with the unique codes and custom codes count
        onInputChange("explicitCodes", uniqueCodes.join("\n"))
        onInputChange("customCodesCount", uniqueCodes.length)
        setCodesSource("file") // Mark that codes came from file
        setUploadedFileName(file.name) // Store the uploaded file name
      } catch (error) {
        showPromptDialog({
          title: "File Read Error",
          message:
            "There was an error reading the file. Please ensure it's a valid text file.",
          showYesBtn: true,
          showNoBtn: false,
          yesLabel: "OK",
          onConfirm: () => {
            resetFileUpload()
          },
        })
      }
    }
    reader.readAsText(file)
  }

  // Handle manual code changes and update custom codes count
  const handleExplicitCodesChange = (value) => {
    setCodesSource("manual") // Mark that codes came from manual entry
    setUploadedFileName("") // Clear uploaded file name when typing manually

    // Always update the raw text immediately for user typing experience
    onInputChange("explicitCodes", value)

    // Process codes for counting (remove empty lines and get unique count)
    const rawCodes = value
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0)

    const uniqueCodes = [...new Set(rawCodes)]
    onInputChange("customCodesCount", uniqueCodes.length) // Use separate field
  }

  return (
    <div className="mb-32">
      {/* Product Panel */}
      <Panel
        variant="admin_info"
        title="Product"
        collapsible={true}
        defaultOpen={true}
      >
        <DropDownSelect
          variant="admin"
          options={productOptions}
          value={
            formData.productId
              ? productOptions.filter((opt) => opt.value === formData.productId)
              : []
          }
          onChange={(values) => {
            const selectedProduct = values?.[0]?.value || ""
            onInputChange("productId", selectedProduct)
          }}
          placeholder="Select a product..."
          error={errors.productId}
          className="w-full"
          searchEnabled
          showCheckbox={false}
          loading={loadingProducts}
        />
      </Panel>

      {/* Seats Panel */}
      <Panel
        variant="admin_info"
        title="Seats"
        collapsible={true}
        defaultOpen={false}
        forceOpen={!!errors.seats}
      >
        <div>
          <Typography variant="admin_label">
            Seats per License *
          </Typography>

          <Input
            variant="admin"
            type="number"
            value={formData.seats}
            onChange={(e) => onInputChange("seats", e.target.value)}
            min={1}
            error={errors.seats}
            className="w-full max-w-xs"
          />
        </div>
      </Panel>

      {/* Users per License Panel */}
      <Panel
        variant="admin_info"
        title="Users per License"
        collapsible={true}
        defaultOpen={false}
        forceOpen={!!errors.users_per_license}
      >
        <div>
          <Typography variant="admin_label">
            Users per License
          </Typography>

          <Typography variant="admin_txt2_dim" className="block mt-0.5 mb-2">
            Maximum distinct users that can activate each generated license. 0 = unlimited (only seats limit applies).
          </Typography>

          <Input
            variant="admin"
            type="number"
            value={formData.users_per_license}
            onChange={(e) => onInputChange("users_per_license", e.target.value)}
            min={0}
            error={errors.users_per_license}
            className="w-full max-w-xs"
          />
        </div>
      </Panel>

      {/* Validity Panel */}
      <Panel
        variant="admin_info"
        title="Validity"
        collapsible={true}
        defaultOpen={false}
        forceOpen={!!errors.validity}
      >
        <div>
          <Typography variant="admin_label">
            License Validity
          </Typography>

          <RadioGroup
            variant="admin"
            value={formData.validityType}
            onChange={(value) => onInputChange("validityType", value)}
            options={[
              { value: "lifetime", label: "Lifetime" },
              { value: "days", label: "Number of Days" },
            ]}
            className="mb-3"
          />

          {formData.validityType === "days" && (
            <Input
              variant="admin"
              type="number"
              value={formData.validity}
              onChange={(e) => onInputChange("validity", e.target.value)}
              min={1}
              placeholder="365"
              error={errors.validity}
              className="w-full max-w-[200px]"
            />
          )}
        </div>
      </Panel>

      {/* Supported Platforms Panel */}
      <Panel
        variant="admin_info"
        title="Supported Platforms"
        collapsible={true}
        defaultOpen={false}
        forceOpen={!!errors.supportedPlatforms}
      >
        <div>
          <Typography variant="admin_label">
            Platform Restrictions
          </Typography>

          <Typography
            variant="admin_txt2_dim"
            className="block mb-4"
          >
            Control which device platforms can activate these licenses. Leave empty or select "All Platforms" to allow any device.
          </Typography>

          <DropDownSelect
            variant="admin"
            options={[
              { value: "any", label: "All Platforms" },
              { value: "ios", label: "iOS" },
              { value: "android", label: "Android" },
              { value: "windows", label: "Windows" },
              { value: "linux", label: "Linux" },
              { value: "macos", label: "macOS" },
              { value: "web", label: "Web" },
            ]}
            value={(() => {
              const platforms = formData.supportedPlatforms || []
              if (platforms.length === 0 || platforms.includes("any")) {
                return [{ value: "any", label: "All Platforms" }]
              }
              return platforms.map(platform => ({
                value: platform,
                label: platform === "ios" ? "iOS" :
                       platform === "android" ? "Android" :
                       platform === "windows" ? "Windows" :
                       platform === "linux" ? "Linux" :
                       platform === "macos" ? "macOS" :
                       platform === "web" ? "Web" :
                       platform.charAt(0).toUpperCase() + platform.slice(1)
              }))
            })()}
            onChange={(values) => {
              let selectedPlatforms = values.map(v => v.value)
              const currentPlatforms = formData.supportedPlatforms || []
              
              // Check if user was switching from empty/any state to specific platforms
              if ((currentPlatforms.length === 0 || currentPlatforms.includes("any")) && 
                  selectedPlatforms.includes("any") && 
                  selectedPlatforms.length > 1) {
                // User had empty/any state and clicked on a specific platform
                // Remove "any" and keep only the specific platforms
                selectedPlatforms = selectedPlatforms.filter(p => p !== "any")
              } else if (selectedPlatforms.includes("any") && selectedPlatforms.length > 1) {
                // User selected "any" along with other platforms, keep only "any"
                selectedPlatforms = ["any"]
              }
              
              onInputChange("supportedPlatforms", selectedPlatforms)
            }}
            placeholder="Select supported platforms..."
            error={errors.supportedPlatforms}
            className="w-full"
            multiSelection={true}
            showCheckbox={false}
            showSelectionTick={true}
            searchEnabled
            onValueItemRender={(option) => (
              <div className="flex items-center gap-2">
                {renderPlatformIcon(option.value, "w-4 h-4 text-admin_text_secondary")}
                <span>{option.label}</span>
              </div>
            )}
          />
          <Typography
            variant="admin_txt2_dim"
            className="block mt-2"
          >
            Platform identifiers: ios, android, windows, linux, macos, web
          </Typography>
        </div>
      </Panel>

      {/* Activation Codes Panel */}
      <Panel
        variant="admin_info"
        title="Activation Codes"
        headerContent={(() => {
          if (formData.activationCodeType === "explicit") {
            const count = formData.customCodesCount || 0
            if (formData.explicitCodes && formData.explicitCodes.trim()) {
              const source = codesSource === "file" ? "via file" : "manual"
              return (
                <Typography
                  variant="admin_label"
                  className=" font-medium"
                >
                  {count} ({source})
                </Typography>
              )
            }
            return null
          } else {
            return (
              <Typography
                variant="admin_body"
                className="my-0"
              >
                {formData.count} (auto-generated)
              </Typography>
            )
          }
        })()}
        collapsible={true}
        defaultOpen={false}
        forceOpen={!!(errors.count || errors.codeGenerationModelId || errors.explicitCodes)}
      >
        <div className="space-y-4">
          <Tabs
            variant="admin"
            value={formData.activationCodeType}
            onChange={(value) => onInputChange("activationCodeType", value)}
            tabs={[
              {
                value: "auto",
                label: "Auto-generated",
              },
              {
                value: "explicit", 
                label: "Custom Codes",
              },
            ]}
            className="mb-4"
          />
          
          {formData.activationCodeType === "auto" && (
            <div className="space-y-4">
              {/* Number of Licenses for auto-generated */}
              <div>
                <Typography
                  variant="admin_label"
                  className="mb-1"
                >
                  Number of Licenses *
                </Typography>

                <Input
                  variant="admin"
                  type="number"
                  value={formData.count}
                  onChange={(e) => onInputChange("count", e.target.value)}
                  min={1}
                  max={10000}
                  error={errors.count}
                  className="w-full max-w-xs"
                />
              </div>
              
              {/* Model selection for auto-generated codes */}
              <div>
                <Typography
                  variant="admin_label"
                  className="mb-1"
                >
                  Code Generation Model
                </Typography>

                <DropDownSelect
                  variant="admin"
                  options={[
                    {
                      value: "",
                      label: "Custom pattern...",
                      description: "Define your own code pattern and character set"
                    },
                    ...modelOptions
                  ]}
                  value={(() => {
                    if (!formData.codeGenerationModelId) return []

                    // Try to find the selected model in the options
                    const selectedOption = modelOptions.find((opt) => opt.value === formData.codeGenerationModelId)
                    if (selectedOption) {
                      return [selectedOption]
                    }

                    // If not found in options but we have a selectedModel that matches, create a temporary option
                    if (selectedModel && selectedModel._id === formData.codeGenerationModelId) {
                      return [{
                        value: selectedModel._id,
                        label: selectedModel.name,
                        description: `${selectedModel.codePattern} • ${selectedModel.codeSet}`,
                      }]
                    }

                    return []
                  })()}
                  onChange={(values) => {
                    const selectedModelId = values?.[0]?.value || ""
                    const selectedModelData = modelOptions.find(opt => opt.value === selectedModelId)

                    onInputChange("codeGenerationModelId", selectedModelId)

                    // Update pattern and set if a model is selected
                    if (selectedModelData) {
                      onInputChange("codePattern", selectedModelData.codePattern)
                      onInputChange("codeSet", selectedModelData.codeSet)
                    } else {
                      // Reset to defaults if no model selected
                      onInputChange("codePattern", "XXXX-XXXX")
                      onInputChange("codeSet", "A-Z,0-9")
                    }
                  }}
                  placeholder="Select a model for code generation..."
                  error={errors.codeGenerationModelId}
                  searchEnabled
                  showCheckbox={false}
                  loading={loadingModels}
                  className="w-full"
                />
              </div>
            </div>
          )}
          
          {formData.activationCodeType === "explicit" && (
            <div className="border border-admin_bordered rounded-lg p-4">
              <Tabs
                variant="admin"
                value={customCodesTab}
                onChange={setCustomCodesTab}
                tabs={[
                  { value: "enter", label: "Enter Codes" },
                  { value: "upload", label: "Upload File" },
                ]}
                className="mb-4"
              />

              {customCodesTab === "enter" && (
                <div>
                  <Input
                    variant="admin"
                    multiline={true}
                    value={formData.explicitCodes}
                    onChange={(e) => handleExplicitCodesChange(e.target.value)}
                    placeholder={`XXXX-XXXX-XXXX-XXXX\nYYYY-YYYY-YYYY-YYYY\n...`}
                    error={errors.explicitCodes}
                    rows={6}
                    className="w-full font-mono"
                  />
                  <Typography variant="admin_txt2_dim">
                    Enter codes, one per line
                  </Typography>
                </div>
              )}

              {customCodesTab === "upload" && (
                <div>
                  <FileUpload
                    key={fileUploadKey}
                    variant="admin"
                    onFileChange={handleFileUpload}
                    accept=".txt,.csv"
                    maxFiles={1}
                    label="Upload activation codes file"
                    description="Upload a .txt or .csv file with one activation code per line"
                    className="mb-2"
                  />

                  <Typography variant="admin_txt2_dim">
                    Upload a .txt or .csv file with codes
                  </Typography>

                  {/* Preview of uploaded codes when from file */}
                  {formData.explicitCodes && codesSource === "file" && (
                    <div className="mt-4 p-3 bg-admin_canvas rounded-lg border">
                      <Typography
                        variant="admin_txt2_dim"
                        className="block font-medium mb-2"
                      >
                        Code Preview (showing first{" "}
                        {Math.min(
                          10,
                          formData.explicitCodes
                            .split("\n")
                            .filter((line) => line.trim().length > 0).length
                        )}{" "}
                        codes):
                      </Typography>

                      <div
                        className="max-h-32 text-sm font-mono bg-paper rounded border p-3 overflow-y-auto"
                        style={{ whiteSpace: "pre-wrap" }}
                      >
                        {(() => {
                          const validCodes = formData.explicitCodes
                            .split("\n")
                            .filter((line) => line.trim().length > 0)
                          const previewCodes = validCodes.slice(0, 10)
                          const hasMore = validCodes.length > 10

                          return (
                            previewCodes.join("\n") +
                            (hasMore
                              ? `\n\n... and ${validCodes.length - 10} more codes`
                              : "")
                          )
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </Panel>
    </div>
  )
}

export default SettingsStep
